import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from 'react';
import type { ReactNode } from 'react';
import {
  getDevAuthBootstrapUser,
  logout as apiLogout,
  onAuthFailure,
  type User,
} from '@/admin/lib/api';

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (userInfo: User) => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Restore authentication state from localStorage on mount
  // Actual auth validation happens when making API requests
  useEffect(() => {
    const storedUser = localStorage.getItem('auth_user');
    if (storedUser) {
      try {
        setUser(JSON.parse(storedUser));
      } catch (e) {
        console.error('Failed to parse stored user', e);
        localStorage.removeItem('auth_user');
      }
    } else {
      const devUser = getDevAuthBootstrapUser();
      if (devUser) {
        setUser(devUser);
      }
    }
    setIsLoading(false);
  }, []);

  const login = (userInfo: User) => {
    setUser(userInfo);
    localStorage.setItem('auth_user', JSON.stringify(userInfo));
  };

  // Clear local auth state without calling logout API
  // Used when JWT is already invalid (401 response)
  const clearAuthState = useCallback(() => {
    localStorage.removeItem('auth_user');
    const devUser = getDevAuthBootstrapUser();
    setUser(devUser);
  }, []);

  const logout = async () => {
    // Call logout endpoint to clear cookie and invalidate session
    try {
      await apiLogout();
    } catch (error) {
      console.error('Logout error:', error);
      // Continue with local cleanup even if API call fails
    }

    // Clear local user state
    clearAuthState();
  };

  // Subscribe to auth failure events (401 responses)
  // This handles the case when JWT token expires
  useEffect(() => {
    const unsubscribe = onAuthFailure(() => {
      console.log('Auth failure detected, clearing auth state');
      clearAuthState();
    });
    return unsubscribe;
  }, [clearAuthState]);

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading,
    login,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
